import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Loader2, Send, CheckCircle2 } from "lucide-react";
import { AdminCard, AdminShell, adminInputCls, adminLabelCls, formatApiError } from "@/components/admin-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotUsername() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-username`, { email });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md" data-testid="forgot-username-page">
        <AdminCard
          title="Forgot your username?"
          subtitle={done ? null : "Enter your registered email address and we'll send your username securely to your email."}
        >
          {done ? (
            <div className="text-center" data-testid="fu-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.5} />
              <p className="mt-5 text-base leading-relaxed text-muted">
                If this email is registered, the username has been sent to it. Please check the registered administrator inbox.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label htmlFor="fu-email-input" className={adminLabelCls}>Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input id="fu-email-input" data-testid="fu-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Administrator email" className={`${adminInputCls} pl-10`} />
                </div>
              </div>
              {error && <p data-testid="fu-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>}
              <button type="submit" data-testid="fu-send-button" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Username
              </button>
            </form>
          )}
          <p className="mt-7 text-center text-sm">
            <Link to="/admin" data-testid="fu-back-login" className="text-muted transition-colors hover:text-navy">← Return to Admin Login</Link>
          </p>
        </AdminCard>
      </motion.div>
    </AdminShell>
  );
}
